import { Link, NavLink } from 'react-router-dom';
import { SignedIn, SignedOut, UserButton, useUser } from '../../lib/auth';
import { useQuery } from '@tanstack/react-query';
import { Heart, LayoutDashboard, Menu, MessageSquare, X } from 'lucide-react';
import { useState } from 'react';
import { TID, NAV_LINKS, BRAND } from '@yalidavet/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isSignedIn } = useUser();

  const { data: unread } = useQuery({
    queryKey: ['unread-count'],
    queryFn: api.unreadCount,
    enabled: !!isSignedIn,
    refetchInterval: 30_000,
  });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.me(), enabled: !!isSignedIn, staleTime: 5 * 60_000 });
  const isAdmin = me?.role === 'admin';

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
      isActive 
        ? 'bg-burgundy-100 text-burgundy-800 font-semibold' 
        : 'text-yali-700 hover:bg-burgundy-50 hover:text-burgundy-900',
    );

  const testIdFor: Record<string, string> = {
    home: TID.navHome,
    listings: TID.navListings,
    messages: TID.navMessages,
    profile: TID.navProfile,
  };

  return (
    <header
      className="sticky top-0 z-50 border-b-2 border-gold-200 bg-gradient-to-r from-white via-cream-50 to-white/95 backdrop-blur supports-[backdrop-filter]:bg-cream-50/80 shadow-sm"
      data-testid={TID.navbar}
    >
      <nav className="yd-container flex h-16 items-center justify-between gap-4">
        {/* Logo — circular brand mark */}
        <Link to="/" className="flex items-center gap-3 group" data-testid={TID.navLogo} aria-label={BRAND.name}>
          <img
            src="/logo.jpeg"
            alt=""
            aria-hidden
            className="h-12 w-12 rounded-full object-cover ring-2 ring-gold-300/70 ring-offset-2 ring-offset-cream-50 shadow-burgundy transition-all duration-500 group-hover:ring-gold-500 group-hover:rotate-6"
          />
          <span className="font-display text-xl font-semibold tracking-[0.18em] uppercase text-burgundy-900 transition-colors group-hover:text-burgundy-700">
            Yalıda <span className="text-gold-600">Davet</span>
          </span>
        </Link>

        {/* Desktop links */}
        <ul className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.key}>
              <NavLink to={link.to} className={linkClass} data-testid={testIdFor[link.key]} end={link.to === '/'}>
                <span className="relative">
                  {link.label}
                  {link.key === 'messages' && (unread?.count ?? 0) > 0 && (
                    <span
                      className="absolute -right-4 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                      data-testid="nav-unread-badge"
                    >
                      {unread!.count > 9 ? '9+' : unread!.count}
                    </span>
                  )}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Auth state — Burgundy theme */}
        <div className="flex items-center gap-3">
          <SignedOut>
            <Link to="/giris" className="yd-btn-primary" data-testid={TID.navSignIn}>
              Giriş Yap
            </Link>
          </SignedOut>

          <SignedIn>
            {isAdmin && (
              <Link to="/admin" className="yd-btn-ghost hidden sm:inline-flex text-burgundy-700" data-testid="nav-admin">
                <LayoutDashboard className="h-4 w-4" aria-hidden />
                Yönetim
              </Link>
            )}
            <Link
              to="/favoriler"
              className="yd-btn-secondary hidden sm:inline-flex border-burgundy-200 hover:bg-burgundy-50"
              data-testid={TID.navFavorites}
            >
              <Heart className="h-4 w-4" aria-hidden />
              Favoriler
            </Link>
            <Link to="/mesajlar" className="yd-btn-ghost md:hidden text-burgundy-700" aria-label="Mesajlar">
              <MessageSquare className="h-5 w-5" aria-hidden />
            </Link>
            <div data-testid={TID.navAvatar}>
              <UserButton
                afterSignOutUrl="/"
                appearance={{ elements: { avatarBox: 'h-10 w-10 rounded-lg' } }}
              />
            </div>
          </SignedIn>

          <button
            type="button"
            className="yd-btn-ghost md:hidden text-burgundy-700 hover:bg-burgundy-50"
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-label="Menüyü aç/kapat"
            data-testid={TID.navMobileToggle}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer — warm gradient */}
      {mobileOpen && (
        <div className="border-t-2 border-gold-200 bg-gradient-to-b from-cream-50 to-white md:hidden shadow-burgundy" data-testid="nav-mobile-menu">
          <ul className="yd-container flex flex-col py-3 space-y-1">
            {NAV_LINKS.map((link) => (
              <li key={link.key}>
                <NavLink
                  to={link.to}
                  className={linkClass}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`${testIdFor[link.key]}-mobile`}
                  end={link.to === '/'}
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
            <SignedIn>
              <li className="pt-2 border-t border-cream-200">
                <NavLink to="/favoriler" className={linkClass} onClick={() => setMobileOpen(false)}>
                  <Heart className="h-4 w-4" aria-hidden /> Favoriler
                </NavLink>
              </li>
              {isAdmin && (
                <li>
                  <NavLink to="/admin" className={linkClass} onClick={() => setMobileOpen(false)}>
                    <LayoutDashboard className="h-4 w-4" aria-hidden /> Yönetim Paneli
                  </NavLink>
                </li>
              )}
            </SignedIn>
          </ul>
        </div>
      )}
    </header>
  );
}
